import { Box } from '@mui/material';
import {
  VictoryBrushContainer,
  VictoryArea,
  VictoryLine,
  VictoryGroup,
  VictoryAxis,
} from 'victory';

/**
 * Props interface for the Chart component
 */
interface Props {
  /** The retirement age to highlight on the chart */
  retireAge: number;
  /** The current age to start the chart from */
  presentAge: number;
}

/**
 * Interface for graph data points
 */
interface IGraphData {
  /** X-axis value (age) */
  x: number;
  /** Y-axis value (amount/value) */
  y: number;
}

/**
 * Chart component for displaying lifetime goals visualization
 * Shows three different investment areas (returns, government, savings) over time
 *
 * @param props - Component props containing retirement and present age
 * @returns JSX element representing the lifetime goals chart
 */
const Chart = (props: Props): JSX.Element => {
  const { retireAge, presentAge } = props;

  /**
   * Generates graph data points for the specified area type
   * @param currentAge - The starting age for the graph
   * @param areaType - The type of investment area ('returns', 'government', 'savings')
   * @returns Array of graph data points with x (age) and y (value) coordinates
   */
  const generatGraphObject = (
    currentAge: number,
    areaType: string,
  ): IGraphData[] => {
    const numberOfNode = 13;
    const maxAge = 80;
    const differenceValue = (maxAge - currentAge) / numberOfNode;

    // Predefined value arrays for different investment areas
    const returns = [3, 5, 9, 16, 27, 44, 64, 90, 120, 155, 192, 232, 269];
    const government = [2, 3, 5, 10, 18, 28, 41, 59, 80, 107, 136, 166, 202];
    const savings = [1, 1, 2, 5, 10, 15, 22, 30, 42, 57, 76, 102, 130];

    // Select the appropriate value array based on area type
    const yAxisArry =
      areaType === 'returns'
        ? returns
        : areaType === 'government'
        ? government
        : savings;

    const GraphData: IGraphData[] = [];

    // Generate data points for each node
    for (let nodeIndex = 0; nodeIndex < numberOfNode; nodeIndex++) {
      if (nodeIndex < 1) {
        GraphData[nodeIndex] = {
          x: currentAge - 1,
          y: nodeIndex > 0 ? yAxisArry[nodeIndex - 1] : 0,
        };
        currentAge += differenceValue;
      } else {
        currentAge += differenceValue;
        GraphData[nodeIndex] = {
          x: currentAge - 1,
          y: nodeIndex > 0 ? yAxisArry[nodeIndex - 1] : 0,
        };
      }
    }
    return GraphData;
  };

  return (
    <Box maxWidth={500}>
      <VictoryGroup
        height={350}
        width={450}
        containerComponent={
          <VictoryBrushContainer
            allowDrag={false}
            allowResize={false}
            brushDomain={{
              x: [retireAge - 1, retireAge],
            }}
            brushStyle={{
              stroke: '#1A6A73',
              fill: '#1A6A73',
            }}
          />
        }
      >
        {/* Returns investment area */}
        <VictoryArea
          interpolation="natural"
          data={generatGraphObject(presentAge, 'returns')}
          style={{ data: { fill: '#FADE93' } }}
        />
        <VictoryLine
          data={generatGraphObject(presentAge, 'returns')}
          interpolation="natural"
          style={{
            data: { stroke: '#FADE93' },
          }}
        />

        {/* Government investment area */}
        <VictoryArea
          interpolation="natural"
          data={generatGraphObject(presentAge, 'government')}
          style={{ data: { fill: '#E15B2D' } }}
        />
        <VictoryLine
          data={generatGraphObject(presentAge, 'government')}
          interpolation="natural"
          style={{
            data: { stroke: '#E15B2D' },
          }}
        />

        {/* Savings investment area */}
        <VictoryArea
          interpolation="natural"
          data={generatGraphObject(presentAge, 'savings')}
          style={{ data: { fill: '#78B894' } }}
        />
        <VictoryLine
          data={generatGraphObject(presentAge, 'savings')}
          interpolation="natural"
          style={{
            data: { stroke: '#78B894' },
          }}
        />

        {/* X-axis with age labels */}
        <VictoryAxis
          style={{
            tickLabels: { fontSize: 10 },
          }}
          tickValues={[presentAge, 80]}
          tickFormat={(t) => `Age ${String(t)}`}
        />
      </VictoryGroup>
    </Box>
  );
};

export default Chart;
